import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar.jsx";
import TopBar from "../Navbar/Mainbar.jsx";

/**
 * TicketDetail — the single-ticket page the email "Open the ticket" button links to.
 * Route: /tickets/:id  ->  fetches GET /api/tickets/:id and renders one ticket.
 */

const getBackendUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === "production") return "https://api.leave-management.manurevasolutions.com";
  return "http://localhost:5000";
};
const BASE = getBackendUrl();
const API = `${BASE}/api/tickets`;

const PRIORITY = {
  high:   { label: "High",   color: "#b91c1c", bg: "#fee2e2" },
  medium: { label: "Medium", color: "#b45309", bg: "#fef3c7" },
  low:    { label: "Low",    color: "#1d4ed8", bg: "#dbeafe" },
};
const STATUS = {
  open:        { label: "Open",        color: "#b45309", bg: "#fef3c7" },
  in_progress: { label: "In progress", color: "#1d4ed8", bg: "#dbeafe" },
  resolved:    { label: "Resolved",    color: "#15803d", bg: "#dcfce7" },
  closed:      { label: "Closed",      color: "#4b5563", bg: "#e5e7eb" },
};
const STATUS_ORDER = ["open", "in_progress", "resolved", "closed"];

function toast(msg, ok = true) {
  const s = typeof window !== "undefined" ? window.Swal : null;
  if (s) s.fire({ toast: true, position: "top-end", timer: 2400, showConfirmButton: false,
    icon: ok ? "success" : "error", title: msg });
  else if (!ok) alert(msg);
}

function Pill({ map, k }) {
  const s = map[k] || { label: k, color: "#4b5563", bg: "#e5e7eb" };
  return <span className="tkd-pill" style={{ color: s.color, background: s.bg }}>{s.label}</span>;
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound | forbidden | error
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const r = await fetch(`${API}/${id}`, { credentials: "include" });
      if (r.status === 404) return setState("notfound");
      if (r.status === 403) return setState("forbidden");
      if (!r.ok) return setState("error");
      const data = await r.json();
      setTicket(data.ticket);
      setState("ok");
    } catch { setState("error"); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/${id}/status`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || "Update failed");
      const data = await r.json();
      setTicket(data.ticket);
      toast(`Status → ${STATUS[status]?.label || status}`);
    } catch (e) { toast(e.message || "Couldn't update status", false); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="Admin-auditlogs-container">
      <TopBar />
      <div style={{ display: "flex", minHeight: "100vh", background: "#f0f2f5" }}>
        <div className="mdmanager-navbar" style={{ overflowX: "hidden" }}><Navbar /></div>
        <div className="tkd-root" style={{ flex: 1, minWidth: 0, padding: 24 }}>
          <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
          <style>{CSS}</style>
          <div className="tkd-wrap">{children}</div>
        </div>
      </div>
    </div>
  );

  if (state === "loading") return <Shell><p className="tkd-muted">Loading ticket…</p></Shell>;
  if (state === "notfound") return <Shell><Note title="Ticket not found" body={`No ticket #${id} exists.`} /></Shell>;
  if (state === "forbidden") return <Shell><Note title="No access" body="You can only view tickets you raised, are assigned to, or as a superadmin." /></Shell>;
  if (state === "error") return <Shell><Note title="Something went wrong" body="Couldn't load this ticket. Try again." /></Shell>;

  const t = ticket;
  return (
    <Shell>
      <button className="tkd-back" onClick={() => navigate(-1)}>← Back</button>

      <div className="tkd-card">
        <div className="tkd-head">
          <div>
            <div className="tkd-badges">
              <Pill map={PRIORITY} k={t.priority} />
              <Pill map={STATUS} k={t.status} />
              {t.overdue && <span className="tkd-overdue">Overdue</span>}
            </div>
            <h1 className="tkd-title">{t.category_label} — ticket #{t.id}</h1>
          </div>
        </div>

        <div className="tkd-meta">
          <div><span>Assigned to</span><strong>{t.assignee_name || "—"}</strong></div>
          <div><span>Raised by</span><strong>{t.raised_by_name || "—"}</strong></div>
          <div><span>Team</span><strong>{t.team || "—"}</strong></div>
          <div><span>Raised</span><strong>{(t.created_at || "").slice(0, 10)}</strong></div>
        </div>

        <div className="tkd-section">
          <div className="tkd-label">Message</div>
          <div className="tkd-msg">{t.message}</div>
        </div>

        {(t.attachments || []).length > 0 && (
          <div className="tkd-section">
            <div className="tkd-label">Attachments</div>
            <div className="tkd-att">
              {t.attachments.map((a) => (
                <a key={a.id} className="tkd-attchip" target="_blank" rel="noreferrer"
                  href={`${API}/${t.id}/attachments/${a.id}`}>
                  {a.kind === "image" ? "🖼" : "📄"} {a.original_name || a.filename}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="tkd-section tkd-statusbar">
          <div className="tkd-label" style={{ margin: 0 }}>Set status</div>
          <div className="tkd-btns">
            {STATUS_ORDER.map((s) => (
              <button key={s} disabled={busy || t.status === s}
                className={`tkd-btn ${t.status === s ? "is-current" : ""}`}
                onClick={() => setStatus(s)}>
                {STATUS[s].label}
              </button>
            ))}
          </div>
         
        </div>
      </div>
    </Shell>
  );
}

function Note({ title, body }) {
  return (
    <div className="tkd-note">
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

const CSS = `
.tkd-root{--navy:#101F40;--blue:#2563eb;--ink:#1f2937;--sub:#6b7280;--line:#e8e4df;
  font-family:'DM Sans',system-ui,sans-serif;color:var(--ink)}
.tkd-root *{box-sizing:border-box}
.tkd-wrap{max-width:760px;margin:0 auto}
.tkd-muted{color:var(--sub)}
.tkd-back{appearance:none;border:0;background:none;color:var(--sub);cursor:pointer;
  font:600 14px 'DM Sans';padding:6px 0;margin-bottom:12px}
.tkd-back:hover{color:var(--ink)}
.tkd-card{background:#fff;border:1px solid var(--line);border-radius:16px;padding:26px}
.tkd-badges{display:flex;gap:8px;align-items:center;margin-bottom:8px}
.tkd-pill{display:inline-block;padding:3px 11px;border-radius:999px;font-size:12px;font-weight:700}
.tkd-overdue{color:#b91c1c;background:#fee2e2;font-size:12px;font-weight:700;padding:3px 11px;border-radius:999px}
.tkd-title{margin:0;font-size:22px;font-weight:700;color:var(--navy)}
.tkd-meta{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin:22px 0;
  padding:16px 0;border-top:1px solid #f0f2f5;border-bottom:1px solid #f0f2f5}
.tkd-meta div{display:flex;flex-direction:column;gap:3px}
.tkd-meta span{font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--sub);font-weight:600}
.tkd-meta strong{font-size:14px;color:var(--ink)}
.tkd-section{margin-top:20px}
.tkd-label{font:600 12px 'DM Sans';text-transform:uppercase;letter-spacing:.04em;color:var(--sub);margin-bottom:8px}
.tkd-msg{background:#f7f8fa;border:1px solid #eceff3;border-radius:12px;padding:16px;
  font-size:15px;line-height:1.6;white-space:pre-wrap}
.tkd-att{display:flex;flex-wrap:wrap;gap:8px}
.tkd-attchip{display:inline-flex;align-items:center;gap:6px;text-decoration:none;font-size:13px;
  font-weight:600;color:var(--ink);background:#eef2f7;border:1px solid var(--line);padding:7px 12px;border-radius:9px}
.tkd-attchip:hover{background:#e3e9f2}
.tkd-statusbar{border-top:1px dashed var(--line);padding-top:18px}
.tkd-btns{display:flex;gap:8px;flex-wrap:wrap}
.tkd-btn{appearance:none;cursor:pointer;font:600 13px 'DM Sans';padding:9px 16px;border-radius:10px;
  border:1px solid var(--line);background:#fff;color:var(--ink)}
.tkd-btn:hover:not(:disabled){border-color:#c9cdd3}
.tkd-btn:disabled{opacity:.55;cursor:default}
.tkd-btn.is-current{background:var(--navy);border-color:var(--navy);color:#fff}
.tkd-hint{font-size:12px;color:var(--sub);margin:12px 0 0}
.tkd-note{background:#fff;border:1px solid var(--line);border-radius:16px;padding:28px;max-width:520px}
.tkd-note h3{margin:0 0 6px;color:var(--navy)}
.tkd-note p{margin:0;color:var(--sub)}
@media (max-width:620px){.tkd-meta{grid-template-columns:1fr 1fr}}
`;
